import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from "react";
import { B, fonts, radius, font } from "../../constants/brand";
import { Icon } from "./Icon";

const ToastContext = createContext(null);

const VARIANTS = {
  error:   { border: B.red,    bg: B.red   + "11", icon: "error" },
  success: { border: B.green,  bg: B.green + "11", icon: "success" },
  info:    { border: B.tealDk, bg: B.tealLt,       icon: "info" },
};

const ToastItem = ({ id, message, variant, duration, onDismiss }) => {
  const v = VARIANTS[variant] || VARIANTS.info;
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setTimeout(() => onDismiss(id), duration ?? 4000);
    return () => clearTimeout(timerRef.current);
  }, [id, duration, onDismiss]);

  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 10,
      background: B.surface, border: `1px solid ${v.border}33`,
      borderLeft: `4px solid ${v.border}`,
      borderRadius: radius.xl2, padding: "13px 15px",
      boxShadow: B.shadowMd, minWidth: 280, maxWidth: 380,
      fontFamily: fonts.sans,
      animation: "harvus-toast-in 0.2s ease-out",
    }}>
      <span style={{ color: v.border, flexShrink: 0, marginTop: 1 }}><Icon name={v.icon} size={18} /></span>
      <span style={{ fontSize: font.base, color: B.text, flex: 1, lineHeight: 1.5 }}>{message}</span>
      <button
        onClick={() => onDismiss(id)}
        aria-label="Dismiss"
        style={{ background: "none", border: "none", cursor: "pointer", color: B.textMd, lineHeight: 1, padding: 0, flexShrink: 0, display: "flex" }}>
        <Icon name="close" size={16} />
      </button>
    </div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const add = useCallback((message, variant = "info", duration) => {
    const id = Date.now() + Math.random();
    const defaultDuration = variant === "error" ? 7000 : 4000;
    setToasts(prev => [...prev, { id, message, variant, duration: duration ?? defaultDuration }]);
  }, []);

  // The context value MUST keep a stable identity across renders.
  //
  // Consumers put `toast` in useCallback/useEffect dependency arrays, and this
  // provider re-renders every time a toast is shown or auto-dismissed. Rebuilt
  // fresh on each of those renders, `toast` invalidated every dependent
  // callback and re-fired its effect — so any toast anywhere in the app made
  // every mounted page refetch.
  //
  // Worse, it closed a loop on any loader that reports its own failure: the
  // error toast re-rendered this provider → new `toast` → the loader's effect
  // re-fired → the request failed again → another toast. One timed-out request
  // became a permanent refetch storm (the symptom: the Digital Assets library
  // stuck on "Loading…" behind a stack of identical timeout toasts).
  //
  // `add` is a stable useCallback, so this memo holds for the app's lifetime.
  //
  // It is ALSO callable — `toast(message, 'error')` — because six screens
  // (the Notes app, Quick Note, Harvi writing tools, the Daily Brief widget,
  // ShareFile and two Sage panels) were written that way, and against a plain
  // object every one of those calls threw "toast is not a function": the
  // message the person needed was never shown and the handler stopped there.
  const toast = useMemo(() => Object.assign(
    (msg, variant = "info", opts) => add(msg, ["error", "success", "info"].includes(variant) ? variant : "info", opts?.duration),
    {
      error:   (msg, opts) => add(msg, "error",   opts?.duration),
      success: (msg, opts) => add(msg, "success", opts?.duration),
      info:    (msg, opts) => add(msg, "info",    opts?.duration),
    },
  ), [add]);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <style>{`
        @keyframes harvus-toast-in {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div role="region" aria-label="Notifications" style={{
        position: "fixed", bottom: 24, right: 24, zIndex: 9999,
        display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-end",
        pointerEvents: "none",
      }}>
        {toasts.map(t => (
          // role="alert" (assertive) for errors, role="status" (polite) otherwise.
          <div key={t.id} role={t.variant === "error" ? "alert" : "status"} aria-atomic="true" style={{ pointerEvents: "auto" }}>
            <ToastItem id={t.id} message={t.message} variant={t.variant} duration={t.duration} onDismiss={dismiss} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  // Memoized for the same reason as the provider value above: a consumer that
  // keeps the whole hook result (`const t = useToast()`) rather than
  // destructuring `toast` off it must get a stable object too.
  const value = useMemo(() => ({ toast: ctx }), [ctx]);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return value;
};
